import { useState } from "react";
import { useLocation } from "wouter";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2, Sparkles } from "lucide-react";
import { apiClient } from "@/lib/api-client";
import { useToast } from "@/hooks/use-toast";
import { getErrorMessage, logApiError } from "@/lib/errors";

interface PaywallModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The feature the user attempted to use, for tailored copy */
    feature: "comparison" | "supplement";
}

const FEATURE_LABELS: Record<PaywallModalProps["feature"], string> = {
    comparison: "comparisons",
    supplement: "supplements",
};

/**
 * Paywall shown when a user without a payment method on file tries to run a
 * usage-based feature (comparison/supplement). Free-tier users can sign up
 * without a card, but must add one before running these features.
 */
export function PaywallModal({ open, onOpenChange, feature }: PaywallModalProps) {
    const [, setLocation] = useLocation();
    const { toast } = useToast();
    const [isRedirecting, setIsRedirecting] = useState(false);

    const featureLabel = FEATURE_LABELS[feature];

    const handleAddPaymentMethod = async () => {
        setIsRedirecting(true);
        try {
            const baseUrl = window.location.origin;
            const result = await apiClient.billing.createBillingPortalSession({
                createBillingPortalSessionRequest: {
                    returnUrl: `${baseUrl}${window.location.pathname}${window.location.search}`,
                },
            });

            if (result.url) {
                window.location.href = result.url;
                return;
            }
            throw new Error("No billing portal URL received");
        } catch (error) {
            logApiError(error, "Paywall add payment method");
            toast({
                title: "Error",
                description: getErrorMessage(error),
                variant: "destructive",
            });
            setIsRedirecting(false);
        }
    };

    const handleViewPlans = () => {
        onOpenChange(false);
        setLocation("/settings?tab=plans");
    };

    // Reset transient redirect state whenever the dialog closes (Esc / click
    // outside / programmatic close). Otherwise a stuck `isRedirecting=true` would
    // leave the buttons disabled/spinning the next time the modal is reopened.
    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            setIsRedirecting(false);
        }
        onOpenChange(nextOpen);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                            <Sparkles className="h-4 w-4 text-primary" />
                        </div>
                        <DialogTitle>Add a payment method to continue</DialogTitle>
                    </div>
                    <DialogDescription className="pt-2">
                        Running {featureLabel} uses pay-as-you-go credits. Add a payment method to
                        unlock {featureLabel} on your current plan &mdash; you&apos;ll only be
                        charged for what you use. You can also upgrade to a paid plan.
                    </DialogDescription>
                </DialogHeader>

                <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button variant="outline" onClick={handleViewPlans} disabled={isRedirecting}>
                        View plans
                    </Button>
                    <Button onClick={handleAddPaymentMethod} disabled={isRedirecting}>
                        {isRedirecting ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <CreditCard className="mr-2 h-4 w-4" />
                        )}
                        Add payment method
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
